import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Copy, ExternalLink, Percent, Tag } from 'lucide-react';
import { Deal } from '@/hooks/useDeals';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isPast } from 'date-fns';

interface DealCardProps {
  deal: Deal;
  compact?: boolean;
}

export const DealCard = ({ deal, compact = false }: DealCardProps) => {
  const isExpiringSoon = deal.expiry_date && 
    !isPast(new Date(deal.expiry_date)) &&
    new Date(deal.expiry_date).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;

  const handleCopyCode = () => {
    if (deal.discount_code) {
      navigator.clipboard.writeText(deal.discount_code);
      toast.success('Discount code copied!');
    }
  };

  const handleVisitDeal = () => {
    const url = deal.deal_url || deal.partner?.partner_name;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (compact) {
    return (
      <Card className="overflow-hidden border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {deal.partner?.logo_url && (
              <img
                src={deal.partner.logo_url}
                alt={deal.partner.partner_name}
                className="w-10 h-10 object-contain rounded"
              />
            )}
            <div className="min-w-0">
              <p className="font-medium truncate">{deal.title}</p>
              {deal.discount_percentage && (
                <Badge variant="secondary" className="mt-1">
                  <Percent className="w-3 h-3 mr-1" />
                  {deal.discount_percentage}% OFF
                </Badge>
              )}
            </div>
          </div>
          {deal.discount_code ? (
            <Button size="sm" variant="outline" onClick={handleCopyCode}>
              <Copy className="w-4 h-4 mr-1" />
              {deal.discount_code}
            </Button>
          ) : (
            <Button size="sm" onClick={handleVisitDeal}>
              Get Deal
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`overflow-hidden transition-all hover:shadow-lg ${deal.is_featured ? 'border-primary ring-1 ring-primary/20' : ''}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            {deal.partner?.logo_url && (
              <img
                src={deal.partner.logo_url}
                alt={deal.partner.partner_name}
                className="w-16 h-16 object-contain rounded-lg border p-2 bg-background"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {deal.is_featured && (
                  <Badge variant="default" className="bg-primary">Featured</Badge>
                )}
                {isExpiringSoon && (
                  <Badge variant="destructive">
                    <Clock className="w-3 h-3 mr-1" />
                    Ending Soon
                  </Badge>
                )}
                {deal.partner?.category && (
                  <Badge variant="outline">{deal.partner.category}</Badge>
                )}
              </div>
              
              <h3 className="font-semibold text-lg mt-2">{deal.title}</h3>
              {deal.partner?.partner_name && (
                <p className="text-sm text-muted-foreground">via {deal.partner.partner_name}</p>
              )}
              
              {deal.description && (
                <p className="text-muted-foreground mt-2 line-clamp-2">{deal.description}</p>
              )}
              
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                {deal.discount_percentage && (
                  <div className="flex items-center gap-1 text-primary font-bold text-xl">
                    <Percent className="w-5 h-5" />
                    {deal.discount_percentage}% OFF
                  </div>
                )}
                {deal.discount_amount && (
                  <div className="flex items-center gap-1 text-primary font-bold text-xl">
                    <Tag className="w-5 h-5" />
                    {deal.currency} {deal.discount_amount} OFF
                  </div>
                )}
                
                {deal.expiry_date && (
                  <p className="text-sm text-muted-foreground">
                    Expires {formatDistanceToNow(new Date(deal.expiry_date), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3 mt-6 pt-4 border-t">
          {deal.discount_code && (
            <Button variant="outline" className="flex-1" onClick={handleCopyCode}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Code: {deal.discount_code}
            </Button>
          )}
          <Button className="flex-1" onClick={handleVisitDeal}>
            <ExternalLink className="w-4 h-4 mr-2" />
            {deal.discount_code ? 'Visit Site' : 'Get Deal'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
